import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { apiFetch, ApiClientError } from "../lib/apiClient";
import { QuestionDisplayCard } from "../components/display/QuestionDisplayCard";
import { StandbyScreen } from "../components/display/StandbyScreen";
import type { DisplayStateResponse } from "../types";

const POLL_INTERVAL_MS = 3000;

export function DisplayPage() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<DisplayStateResponse | null>(null);
  const [forbidden, setForbidden] = useState(false);
  // Keep latest values for the interval callback without re-subscribing.
  const stop = useRef(false);

  // OBS Browser Source needs a fully transparent page so the overlay composites
  // over the stream. The global body background would otherwise paint gray.
  useEffect(() => {
    document.body.classList.add("display-mode");
    return () => document.body.classList.remove("display-mode");
  }, []);

  useEffect(() => {
    if (!slug || !token) {
      setForbidden(true);
      return;
    }
    stop.current = false;

    const endpoint = `/api/public/webinars/${encodeURIComponent(
      slug,
    )}/display-state?token=${encodeURIComponent(token)}`;

    async function poll() {
      try {
        const data = await apiFetch<DisplayStateResponse>(endpoint);
        if (!stop.current) {
          setState(data);
          setForbidden(false);
        }
      } catch (err) {
        if (stop.current) return;
        // Only a forbidden token is terminal. Transient failures (5xx /
        // network) keep the last rendered frame so a blip doesn't flash an
        // error onto the live stream.
        if (err instanceof ApiClientError && err.httpStatus === 403) {
          setForbidden(true);
        }
      }
    }

    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      stop.current = true;
      clearInterval(id);
    };
  }, [slug, token]);

  if (forbidden) {
    return (
      <div className="display-root">
        <div className="display-error">表示できません（display_tokenを確認してください）</div>
      </div>
    );
  }

  const questions = state?.questions ?? [];
  const showQuestions = state?.mode === "question" && questions.length > 0;

  return (
    <div className="display-root">
      {showQuestions ? (
        <div
          className={`display-stack display-stack-${Math.min(questions.length, 4)}`}
        >
          {questions.map((q) => (
            <QuestionDisplayCard key={q.id} text={q.display_text} name={q.display_name} />
          ))}
        </div>
      ) : (
        <StandbyScreen />
      )}
    </div>
  );
}
